import { ComponentFactoryResolver, Injectable, Type } from '@angular/core';
import { plural } from 'pluralize';
import { Observable, forkJoin, of } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { Category } from '../../shared/models/lookup-category';
import { DataField } from '../../shared/models/data-field';
import { Lookup } from '../../shared/models/lookup-category';
import { ProcessType } from '../../shared/models/process-type';
import { LayoutItem, TEMPLATE_TYPE } from '../../shared/models/template';
import { Widget, WidgetGroup } from '../../shared/models/widget';
import { CategoryService } from '../../shared/services/category.service';
import { LookupService } from '../../shared/services/lookup.service';
import { SubjectTypeService } from '../../shared/services/subject-type.service';
import { UserService } from 'app/shared/services/user.service';
import { StatusService } from 'app/shared/services/status.service';
import { SubjectCountWidgetSettingsComponent } from './count-widgets/subject-count-widget/subject-count-widget-settings/subject-count-widget-settings.component';
import { TaskCountWidgetSettingsComponent } from './count-widgets/task-count-widget/task-count-widget-settings/task-count-widget-settings.component';
import { CountWidgetSettings } from 'app/shared/models/count-widget-settings';
import { DateHelper } from '../content/misc/date.module';
import { IEntity, IEntityWithLinks } from 'app/shared/interfaces/entity';
import { HtmlWidgetSettingsComponent } from './label-widgets/html-widget/html-widget-settings/html-widget-settings.component';
import { OrganisationService } from 'app/shared/services/organisation.service';
import { ReferralCountWidgetSettingsComponent } from './count-widgets/referral-count-widget/referral-count-widget-settings/referral-count-widget-settings.component';
import { environment } from 'environments/environment';
import { Subject } from 'app/shared/models/subject';
import { Process } from 'app/shared/models/process';
import { SubjectService } from 'app/shared/services/subject.service';
import { SubjectReviewCountWidgetSettingsComponent } from './count-widgets/subject-review-count-widget/subject-review-count-widget-settings/subject-review-count-widget-settings.component';
import { SubjectCountWidgetComponent } from './count-widgets/subject-count-widget/subject-count-widget';
import { MatDialog } from '@angular/material/dialog';
import { DropdownComponent } from './input-widgets/dropdown/dropdown.component';
import { BooleanComponent } from './input-widgets/boolean/boolean.component';
import { NumberComponent } from './input-widgets/number/number.component';
import { TextInputComponent } from './input-widgets/text-input/text-input.component';
import { MemoComponent } from './input-widgets/memo/memo.component';
import { CurrencyComponent } from './input-widgets/currency/currency.component';
import { LineWidgetComponent } from './label-widgets/line-widget/line-widget.component';
import { LabelWidgetComponent } from './label-widgets/label-widget/label-widget.component';
import { FieldValueWidgetComponent } from './label-widgets/field-value-widget/field-value-widget.component';
import { HtmlWidgetComponent } from './label-widgets/html-widget/html-widget.component';
import { ReferralWidgetComponent } from './card-widgets/referral-widget/referral-widget.component';
import { SubjectCardWidgetComponent } from './card-widgets/subject-card-widget.component';
import { LinkedSubjectsWidgetComponent } from './list-widgets/linked-subjects-widget.component';
import { UserSelectionWidgetComponent } from './user-selection-widget/user-selection-widget.component';
import { TaskWidgetComponent } from './task-widgets/task-widget.component';
import { SearchWidgetComponent } from './search-widget/search-widget.component';
import { NoteWidgetComponent } from './note-widgets/note-widget.component';
import { SubjectListWidgetComponent } from './list-widgets/subject-list-widget/subject-list-widget.component';
import { RecentHistoryWidgetComponent } from './list-widgets/recent-history-widget.component';
import { ProcessListWidgetComponent } from './list-widgets/process-list-widget/process-list-widget.component';
import { FileListWidgetComponent } from './list-widgets/file-list-widget/file-list-widget.component';
import { ReferralCountWidgetComponent } from './count-widgets/referral-count-widget/referral-count-widget';
import { SubjectReviewCountWidgetComponent } from './count-widgets/subject-review-count-widget/subject-review-count-widget';
import { TaskCountWidgetComponent } from './count-widgets/task-count-widget/task-count-widget';
import { MarkerListWidgetComponent } from './list-widgets/marker-list-widget/marker-list-widget.component';
import { ReviewListWidgetComponent } from './list-widgets/review-list-widget/review-list-widget.component';
import { AssessmentListWidgetComponent } from './list-widgets/assessment-list-widget/assessment-list-widget.component';
import { AuditListWidgetComponent } from './list-widgets/audit-list-widget/audit-list-widget.component';
import { TaskOverviewChartWidgetComponent } from './chart-widgets/task-overview-chart-widget.component';
import { ReferralCommentsWidgetComponent } from './referral-comments-widget/referral-comments-widget.component';
import { DatepickerComponent } from './input-widgets/datepicker/datepicker.component';
import { ChecklistWidgetComponent } from './list-widgets/checklist-list-widget/checklist-list-widget.component';
import { YesNoComponent } from './input-widgets/yesno/yesno.component';
import { ProcessTypeService } from 'app/shared/services/process-type.service';

@Injectable()
export class WidgetService {
  private DEFAULT_WIDTH = 4;
  private DEFAULT_HEIGHT = 1;

  constructor(
    private resolver: ComponentFactoryResolver,
    // private viewContinaerRef: ViewContainerRef,
    private lookupService: LookupService,
    private categoryService: CategoryService,
    private subjectTypeService: SubjectTypeService,
    private userService: UserService,
    private statusService: StatusService,
    private organisationService: OrganisationService,
    private dateHelper: DateHelper,
    private subjectService: SubjectService,
    private processTypeService: ProcessTypeService,
    public dialog: MatDialog
  ) {}

  /**
   * Takes a component name and returns the factory class
   * Used for dynamic generation of components from component name as string
   * @param {string} component name
   * @returns Component class
   * @memberof WidgetService
   */
  getWidget(name: string) {
    const component = this.getWidgetComponent(name);
    return component || null;
  }

  private getWidgetComponent(name: string) {
    switch (name) {
      case 'YesNoComponent':
        return YesNoComponent;
      case 'TextInputComponent':
        return TextInputComponent;
      case 'NumberComponent':
        return NumberComponent;
      case 'DropdownComponent':
        return DropdownComponent;
      case 'DatepickerComponent':
        return DatepickerComponent;
      case 'BooleanComponent':
        return BooleanComponent;
      case 'MemoComponent':
        return MemoComponent;
      case 'CurrencyComponent':
        return CurrencyComponent;
      case 'LineWidgetComponent':
        return LineWidgetComponent;
      case 'LabelWidgetComponent':
        return LabelWidgetComponent;
      case 'FieldValueWidgetComponent':
        return FieldValueWidgetComponent;
      case 'HtmlWidgetComponent':
        return HtmlWidgetComponent;
      case 'ReferralWidgetComponent':
        return ReferralWidgetComponent;
      case 'SubjectCardWidgetComponent':
        return SubjectCardWidgetComponent;
      case 'LinkedSubjectsWidgetComponent':
        return LinkedSubjectsWidgetComponent;
      case 'UserSelectionWidgetComponent':
        return UserSelectionWidgetComponent;
      case 'TaskWidgetComponent':
        return TaskWidgetComponent;
      case 'SearchWidgetComponent':
        return SearchWidgetComponent;
      case 'NoteWidgetComponent':
        return NoteWidgetComponent;
      case 'SubjectListWidgetComponent':
        return SubjectListWidgetComponent;
      case 'RecentHistoryWidgetComponent':
        return RecentHistoryWidgetComponent;
      case 'ProcessListWidgetComponent':
        return ProcessListWidgetComponent;
      case 'ChecklistWidgetComponent':
        return ChecklistWidgetComponent;
      case 'FileListWidgetComponent':
        return FileListWidgetComponent;
      case 'SubjectCountWidgetComponent':
      case 'SubjectCountByStatusWidgetComponent':
      case 'SubjectCountByCaseTypeWidgetComponent':
      case 'SubjectCountByCategoryWidgetComponent':
        return SubjectCountWidgetComponent;
      case 'ReferralCountWidgetComponent':
        return ReferralCountWidgetComponent;

      case 'SubjectReviewCountWidgetComponent':
        return SubjectReviewCountWidgetComponent;
      case 'TaskCountWidgetComponent':
        return TaskCountWidgetComponent;

      case 'MarkerListWidgetComponent':
        return MarkerListWidgetComponent;
      case 'ReviewListWidgetComponent':
        return ReviewListWidgetComponent;
      case 'AssessmentListWidgetComponent':
        return AssessmentListWidgetComponent;
      case 'AuditListWidgetComponent':
        return AuditListWidgetComponent;
      case 'TaskOverviewChartWidgetComponent':
        return TaskOverviewChartWidgetComponent;
      case 'ReferralCommentsWidgetComponent':
        return ReferralCommentsWidgetComponent;
      default: {
        console.log('Failed to map ' + name + ' to component class');
      }
    }
    return undefined;
  }

  private getWidgetSelector(name: string) {
    switch (name) {
      case 'YesNoComponent':
        return 'app-yesno';
      case 'TextInputComponent':
        return 'app-text-input';
      case 'NumberComponent':
        return 'app-number';
      case 'DropdownComponent':
        return 'app-dropdown';
      case 'DatepickerComponent':
        return 'app-datepicker';
      case 'BooleanComponent':
        return 'app-boolean';
      case 'MemoComponent':
        return 'app-memo';
      case 'CurrencyComponent':
        return 'app-currency';
      case 'LineWidgetComponent':
        return 'line-widget';
      case 'LabelWidgetComponent':
        return 'label-widget';
      case 'FieldValueWidgetComponent':
        return 'field-value-widget';
      case 'HtmlWidgetComponent':
        return 'html-widget';
      case 'ReferralWidgetComponent':
        return 'referral-widget';
      case 'SubjectCardWidgetComponent':
        return 'subject-card-widget';
      case 'LinkedSubjectsWidgetComponent':
        return 'linked-subjects-widget';
      case 'UserSelectionWidgetComponent':
        return 'app-user-selection-widget';
      case 'TaskWidgetComponent':
        return 'task-widget';
      case 'SearchWidgetComponent':
        return 'app-search-widget';
      case 'NoteWidgetComponent':
        return 'note-widget';
      case 'SubjectListWidgetComponent':
        return 'subject-list-widget';
      case 'RecentHistoryWidgetComponent':
        return 'recent-history-widget';
      case 'ProcessListWidgetComponent':
        return 'process-list-widget';
      case 'ChecklistWidgetComponent':
        return 'checklist-list-widget';
      case 'FileListWidgetComponent':
        return 'file-list-widget';
      case 'SubjectCountWidgetComponent':
        return 'subject-count-widget';
      case 'ReferralCountWidgetComponent':
        return 'referral-count-widget';
      case 'SubjectCountByStatusWidgetComponent':
        return 'subject-count-widget';
      case 'SubjectCountByCaseTypeWidgetComponent':
        return 'subject-count-widget';
      case 'SubjectCountByCategoryWidgetComponent':
        return 'subject-count-widget';

      case 'SubjectReviewCountWidgetComponent':
        return 'subject-review-count-widget';
      case 'TaskCountWidgetComponent':
        return 'task-count-widget';

      case 'MarkerListWidgetComponent':
        return 'marker-list-widget';
      case 'ReviewListWidgetComponent':
        return 'review-list-widget';
      case 'AssessmentListWidgetComponent':
        return 'assessment-list-widget';
      case 'AuditListWidgetComponent':
        return 'audit-list-widget';
      case 'TaskOverviewChartWidgetComponent':
        return 'task-overview-chart-widget';
      case 'ReferralCommentsWidgetComponent':
        return 'app-referral-comments-widget';
      default: {
        console.log('Failed to map ' + name + ' to component class name');
      }
    }
    return '';
  }

  getWidgetType(type: string) {
    switch (type.toString().toLowerCase()) {
      case 'yesno':
        return 'YesNoComponent';
      case 'text':
        return 'TextInputComponent';
      case 'date':
        return 'DatepickerComponent';
      case 'number':
        return 'NumberComponent';
      case 'boolean':
        return 'BooleanComponent';
      case 'memo':
        return 'MemoComponent';
      case 'currency':
        return 'CurrencyComponent';
      case 'line':
        return 'LineWidgetComponent';
      case 'label':
        return 'LabelWidgetComponent';
      case 'calculated':
        return 'NumberComponent';
      case 'fieldvalue':
        return 'FieldValueWidgetComponent';
      case 'html':
        return 'HtmlWidgetComponent';
      case 'referralcard':
        return 'ReferralWidgetComponent';
      case 'subjectcard':
        return 'SubjectCardWidgetComponent';
      case 'linkedsubjects':
        return 'LinkedSubjectsWidgetComponent';
      case 'assessmentlist':
        return 'AssessmentListWidgetComponent';
      case 'auditlist':
        return 'AuditListWidgetComponent';
      case 'markerlist':
        return 'MarkerListWidgetComponent';
      case 'processlist':
        return 'ProcessListWidgetComponent';
      case 'checklist':
        return 'ChecklistWidgetComponent';
      case 'reviewlist':
        return 'ReviewListWidgetComponent';
      case 'filelist':
        return 'FileListWidgetComponent';
      case 'referralcomments':
        return 'ReferralCommentsWidgetComponent';
      case 'category':
        return 'DropdownComponent';
      case 'status':
        return 'DropdownComponent';
      case 'user':
        return 'DropdownComponent';
      case 'organisation':
        return 'DropdownComponent';
      default:
        if (/^\d+$/.test(type)) {
          // if type contains a number then likely to be a guid or id corresponding to a lookuptype, therefore use a dropdown component
          return 'DropdownComponent';
        } else {
          return type;
        }
    }
  }

  /**
   * Takes a data field and returns a widget with the same properties
   *
   * @param {DataField} dataField
   * @param {{cols: number, rows: number, x?: number, y?: number}} config
   * @returns {Widget}
   */
  fieldAsWidget(
    subject: Subject | Process | undefined,
    dataField: DataField,
    config: { cols: number; rows: number; x?: number; y?: number },
    subjectTypeId?: number,
    categoryId?: number,
    overrideType?: string
  ) {
    const widget = {
      id: dataField.id,
      name: dataField.name,
      title: dataField.label,
      metadata: {
        id: dataField.id, // to keep track of widget metadata
        min: dataField.min || '-9999999999',
        max: dataField.max,
        label: dataField.label,
        required: dataField.required, // dataField.required,
        value: dataField.default,
        readonly: dataField.readonly,
      },
      config,
      type: overrideType || dataField.dataType,
    } as Widget;

    if (widget.metadata.value) {
      // if is a date type and a default value set in value field then make sure it has been converted to a valid date
      if (widget.type.toString().toLowerCase() === 'date') {
        widget.metadata.value = this.dateHelper
          .relativeToAbsolute(widget.metadata.value)
          .toDate();
      }
    }

    if (widget.type.toString().toLowerCase() === 'boolean') {
      // make sure boolean has a default of false if no default specified...
      widget.metadata.value = widget.metadata.value || 'false';
      const val = widget.metadata.value.toString().toLowerCase();
      widget.metadata.value = val === 'true' || val === 'yes' ? true : false;
    }

    if (widget.type.toString().toLowerCase() === 'category') {
      widget.metadata.possibleValues = [];
      let categories: Category[] = [];
      this.categoryService
        .getCategoriesBySubjectType(subjectTypeId)
        .pipe(take(1))
        .subscribe((response) => {
          categories = [...response];
          // tslint:disable-next-line:only-arrow-functions
          categories.sort(function (a, b) {
            return a.description.localeCompare(b.description);
          });
          if (!dataField.required) {
            widget.metadata.possibleValues.push({ id: null, value: 'None' });
          }
          categories.forEach((category) => {
            widget.metadata.possibleValues.push({
              id: category.id,
              value: category.description,
            });
          });
        });
    }

    if (widget.type.toString().toLowerCase() === 'user') {
      widget.metadata.possibleValues = [];
      if (!dataField.required) {
        widget.metadata.possibleValues.push({ id: null, value: 'None' });
      }

      // Need to detect if subject is Subject or Process or Task.
      // This is where we need the OrganisationId (from Subject or User) to limit the list of selected users
      // if LimitAccessToSubjectByOrg is true.

      // This code is not correct as yet because if user is admin then they should not have OrgId assigned,
      // therefore a list of ALL active users will be displayed (and any user could be selected),
      // but if subject has orgId specified and LimitAccess is true
      // we need to limit that list of users. So we need to subject so we can check the subject's organisationId.
      let users$;
      if (subject) {
        if (subject instanceof Subject) {
          const sub = subject as Subject;
          if (sub && sub.subjectOrganisations.length > 0) {
            users$ = this.userService.getActiveUsersForOrganisation(
              sub.subjectOrganisations[0].organisationId,
              false
            );
          }
        }
        if (subject instanceof Process) {
          const proc = subject as Process;
          if (proc && proc.organisationId > 0) {
            users$ = this.userService.getActiveUsersForOrganisation(
              proc.organisationId,
              false
            );
          }
        }
        if (!users$) {
          users$ = this.userService.getActiveUsers();
        }
        // subscribe, filter (based on category) and set possible values
        users$.pipe(take(1)).subscribe((users) => {
          if (categoryId) {
            const category = this.categoryService.getByIdFromCache(categoryId);
            if (
              category &&
              category.autoRestrict &&
              category.categoryUserSecurity.length > 0
            ) {
              const selectedUsers = category.categoryUserSecurity.map(
                (i) => i.userId
              );
              users = users.filter((i) => selectedUsers.indexOf(i.id) > -1);
            }
          }
          users.forEach((user) => {
            widget.metadata.possibleValues.push({
              id: user.id,
              value: user.fullName,
            });
          });
        });
      }
    }

    if (widget.type.toString().toLowerCase() === 'organisation') {
      if (environment.limitAccessToSubjectsByOrg) {
        widget.metadata.readonly = true;
      }
      widget.metadata.possibleValues = [];
      if (!dataField.required) {
        widget.metadata.possibleValues.push({ id: null, value: 'None' });
      }
      this.organisationService
        .getAllData()
        .pipe(take(1))
        .subscribe((response) => {
          if (response && response.length > 0) {
            const organisations = response.filter(
              (organisation) => organisation.hideRecord !== true
            );
            organisations.sort((a, b) => a.name.localeCompare(b.name));
            organisations.forEach((organisation) => {
              widget.metadata.possibleValues.push({
                id: organisation.id,
                value: organisation.name,
              });
            });
          }
        });
    }

    if (widget.type.toString().toLowerCase() === 'status') {
      widget.metadata.possibleValues = [];
      if (!dataField.required) {
        widget.metadata.possibleValues.push({ id: null, value: 'None' });
      }
      this.statusService
        .getStatusListForCategory(categoryId)
        .pipe(take(1))
        .subscribe((response) => {
          if (response?.length > 0) {
            let statuses = response;
            const user = this.userService.getCurrentUser();
            if (environment.requirePermissionToCloseCases && !user?.canCloseCases) {
              statuses = statuses.filter((i) => i.stateId !== 1);     
            }
            // tslint:disable-next-line:only-arrow-functions
            statuses.forEach((status) => {
              widget.metadata.possibleValues.push({
                id: 'status' + status.id.toString(),
                value: status.name,
              });
            });
          }
        });
      // .getAllData()
      // .pipe(take(1))
      // .subscribe((response) => {
      //   if (response?.length > 0) {
      //     const statuses = response.filter(
      //       (status) => status.hideRecord !== true
      //     );
      //     // tslint:disable-next-line:only-arrow-functions
      //     statuses.sort((a, b) => a.name.localeCompare(b.name));
      //     statuses.forEach((status) => {
      //       if (
      //         !categoryId ||
      //         (categoryId &&
      //           (!status.statusCategoryLinks ||
      //             status.statusCategoryLinks.length === 0 ||
      //             status.statusCategoryLinks
      //               .map((i) => i.categoryId)
      //               .includes(categoryId)))
      //       ) {
      //         widget.metadata.possibleValues.push({
      //           id: 'status' + status.id.toString(),
      //           value: status.name,
      //         });
      //       }
      //     });
      //   }
      // });
    }

    if (/^\d+$/.test(widget.type.toString())) {
      widget.metadata.possibleValues = [];
      let lookups: Lookup[] = [];
      this.lookupService
        .getAllByLookupType(Number(widget.type))
        .pipe(take(1))
        .subscribe((response) => {
          lookups = [...response?.filter((i) => !i.hideRecord)];
          lookups.sort((a, b) => a.description.localeCompare(b.description));
          if (!dataField.required) {
            widget.metadata.possibleValues.push({ id: null, value: 'None' });
          }
          lookups.forEach((lookup) => {
            if (
              !categoryId ||
              (categoryId &&
                (!lookup.lookupCategories ||
                  lookup.lookupCategories.length === 0 ||
                  lookup.lookupCategories
                    .map((i) => i.categoryId)
                    .includes(categoryId)))
            ) {
              widget.metadata.possibleValues.push({
                id: lookup.id,
                value: lookup.description,
              });
            }
          });
        });
    }

    switch (widget.type.toString().toLowerCase()) {
      case 'label':
      case 'fieldvalue':
        widget.metadata.value = widget.metadata.label;
        break;
      default:
        break;
    }

    // if widget has possible values double check that if a default value has been specified it is set to the correct id
    if (widget && widget.metadata.value) {
      const pv = widget.metadata.possibleValues;
      if (pv) {
        // if possible values are specified then probably a lookup
        // convert the value to a number, just in case
        let val = Number(widget.metadata.value);
        if (isNaN(val)) {
          val = widget.metadata.value;
        }
        if (!pv.find((i) => i.id === val)) {
          const foundItem = pv.find(
            (i) =>
              i.value.toString().toLowerCase() ===
              widget.metadata.value.toString().toLowerCase()
          );
          if (foundItem) {
            widget.metadata.value = foundItem.id;
          }
        } else {
          widget.metadata.value = val;
        }
      }
    }

    return widget;
  }

  widgetsToLayoutItems(widgets: Widget[]): LayoutItem[] {
    const layout: LayoutItem[] = [];
    widgets.forEach((item) => {
      // only process item if is either not configurable, or is configurable and settings have been set.
      // this way any widgets which are in the process of being configured are not included
      if (!item.configurable || (item.configurable && item.metadata.settings)) {
        const layoutItem = new LayoutItem();
        (layoutItem.id = item.id),
          (layoutItem.name = item.name),
          (layoutItem.type = item.type),
          (layoutItem.layoutInformation = {
            x: item.config.x,
            y: item.config.y,
            cols: item.config.cols,
            rows: item.config.rows,
          });
        layoutItem.metadata = {};
        if (item.metadata.subjectId)
          layoutItem.metadata.subjectId = item.metadata.subjectId;
        if (item.metadata.processTypeId)
          layoutItem.metadata.processTypeId = item.metadata.processTypeId;
        if (item.configurable && item.metadata.settings) {
          layoutItem.metadata.settings = item.metadata.settings;
        }
        layoutItem.configurable = item.configurable;
        layout.push(layoutItem);
      }
    });
    return layout;
  }

  layoutItemToWidget(item: LayoutItem, dataField?: DataField): Widget {
    let widget;
    if (dataField) {
      widget = this.fieldAsWidget(
        undefined,
        dataField,
        item.layoutInformation,
        undefined,
        undefined,
        item.type
      );
    } else {
      widget = {
        id: item.id,
        name: item.name,
        metadata: {
          id: item.id,
        },
        config: item.layoutInformation,
        type: item.type,
        configurable: item.configurable,
      } as Widget;
      if (item.metadata) {
        if (item.metadata.subjectId)
          widget.metadata.subjectId = item.metadata.subjectId;
        if (item.metadata.processTypeId)
          widget.metadata.processTypeId = item.metadata.processTypeId;
        if (item.metadata.settings)
          widget.metadata.settings = item.metadata.settings;
      }
    }
    const type = this.getWidgetType(widget.type);
    widget.widget = this.getWidget(type);
    return widget;
  }

  getAvailableSubjectTypeFieldWidgets(
    templateType: string,
    subjectTypeId: number,
    fieldsToExclude?: string[]
  ): Observable<WidgetGroup[]> {
    return forkJoin({
      dataFieldsToExclude: this.processTypeService.getEqualityMonitoringFieldsToExclude(),
      subjectType: this.subjectTypeService.getById(subjectTypeId),
    }).pipe(
      map((res) => {
        return this.getAvailableFieldWidgets(templateType, res.subjectType.dataFields, subjectTypeId, fieldsToExclude, res.dataFieldsToExclude);
      })
    );
  }

  // get widgetgroup for each datafield based on the template type.  If calling for subjectType then include the subjectTypeId so category widget is correctly populated
  getAvailableFieldWidgets(
    templateType: string,
    dataFields: DataField[],
    subjectTypeId?: number,
    fieldsToExclude?: string[],
    dataFieldsToExclude?: DataField[]
  ): WidgetGroup[] {    
    if (!fieldsToExclude)
      fieldsToExclude= [];
    if (!dataFieldsToExclude)
      dataFieldsToExclude= [];

    // setup an array of widget types which need to be created in the widgetgroup for each datafield
    let widgetTypeArray: string[] = [];
    let width = this.DEFAULT_WIDTH;
    const height = this.DEFAULT_HEIGHT;
    switch (templateType) {
      case TEMPLATE_TYPE.CARD:
        widgetTypeArray = ['LABEL', 'FIELDVALUE'];
        break;
      case TEMPLATE_TYPE.REFERRALCARD:
        widgetTypeArray = ['LABEL', 'FIELDVALUE'];
        break;
      case TEMPLATE_TYPE.LIST:
        widgetTypeArray = ['LABEL'];
        width = 2;
        break;
      default:
        break;
    }

    const widgets: WidgetGroup[] = [];
    const excludeFromEdit = [
      'summary',
      'datecreated',
      'createdbyid',
      'primarylink',
      'assessmentscore',
      'linkedsubject',
      'markers',
      'retentiondate',
      'deadlinedatelastprocessed',
      'deadlinedate',
      'deadlinedaysleft',
      'useworkingdays',
      'tasks',
    ];
    const excludeFromCard = [
      'primarylink',
      'linkedsubject',
      'markers',
      'deadlinedatelastprocessed',
      'useworkingdays',
      'tasks',
    ];
    const excludeFromReferral = [
      'summary',
      'datecreated',
      'createdbyid',
      'primarylink',
      'assessmentscore',
      'linkedsubject',
      'retentiondate',
      'markers',
      'deadlinedatelastprocessed',
      'deadlinedate',
      'deadlinedaysleft',
      'useworkingdays',
    ]; // , 'managerid', 'userid'
    const excludeFromReferralCard = [
      'primarylink',
      'assessmentscore',
      'linkedsubject',
      'markers',
      'deadlinedatelastprocessed',
      'useworkingdays',
      'tasks',
    ]; // , 'managerid', 'userid'

    dataFields.forEach((dataField) => {
      // summary is a calculated field and shouldn't be on the edit form.  Same goes for datecreated and createdby
      if (
        (!fieldsToExclude || fieldsToExclude.indexOf(dataField.name.toLowerCase()) < 0) &&
        !(
          (templateType === TEMPLATE_TYPE.EDIT && excludeFromEdit.indexOf(dataField.name.toLowerCase()) > -1) ||
          (templateType === TEMPLATE_TYPE.CARD && excludeFromCard.indexOf(dataField.name.toLowerCase()) > -1) ||
          (templateType === TEMPLATE_TYPE.REFERRAL && excludeFromReferral.indexOf(dataField.name.toLowerCase()) > -1) ||
          (templateType === TEMPLATE_TYPE.REFERRALCARD &&
            excludeFromReferralCard.indexOf(dataField.name.toLowerCase()) > -1)
        ) &&
        !(dataFieldsToExclude?.findIndex((i) => i.id === dataField.id) > -1)
      ) {
        const widgetGroup = new WidgetGroup();
        widgetGroup.name = dataField.label;

        if (widgetTypeArray.length > 0) {
          widgetTypeArray.forEach((item) => {
            widgetGroup.widgets.push(
              this.fieldAsWidget(
                undefined,
                dataField,
                { cols: width, rows: height },
                subjectTypeId,
                undefined,
                item
              )
            );
          });
        } else {
          widgetGroup.widgets.push(
            this.fieldAsWidget(
              undefined,
              dataField,
              { cols: width, rows: height },
              subjectTypeId,
              undefined
            )
          );
        }
        widgets.push(widgetGroup);
      }
    });

    // sort and return
    // tslint:disable-next-line:only-arrow-functions
    widgets.sort(function (a, b) {
      return a.name.localeCompare(b.name);
    });
    return widgets;
  }

  displaySettings(widget: Widget): Observable<any> {
    let dialogComponent: any;
    const data = widget.metadata.settings || new CountWidgetSettings();
    switch (widget.type.toString().toLowerCase()) {
      case 'subjectcountwidgetcomponent':
        dialogComponent = SubjectCountWidgetSettingsComponent;
        // data.showStatus = false;
        data.showFlags = 1;
        break;
      case 'referralcountwidgetcomponent':
        dialogComponent = ReferralCountWidgetSettingsComponent;
        // data.showStatus = false;
        data.showFlags = 1;
        break;
      case 'subjectcountbystatuswidgetcomponent':
        dialogComponent = SubjectCountWidgetSettingsComponent;
        // data.showStatus = true;
        data.showFlags = 2;
        break;
      case 'subjectcountbycasetypewidgetcomponent':
        dialogComponent = SubjectCountWidgetSettingsComponent;
        data.showFlags = 4;
        break;
      case 'subjectcountbycategorywidgetcomponent':
        dialogComponent = SubjectCountWidgetSettingsComponent;
        data.showFlags = 8;
        break;
      case 'taskcountwidgetcomponent':
        dialogComponent = TaskCountWidgetSettingsComponent;
        break;
      case 'htmlwidgetcomponent':
        dialogComponent = HtmlWidgetSettingsComponent;
        break;
      case 'subjectreviewcountwidgetcomponent':
        dialogComponent = SubjectReviewCountWidgetSettingsComponent;
        break;
      default:
        break;
    }
    if (dialogComponent) {
      const widgetSettingsDialogRef = this.dialog.open(dialogComponent, {
        data,
        disableClose: true,
      });

      return widgetSettingsDialogRef.afterClosed().pipe(
        map((result) => {
          if (result && result.data) {
            widget.metadata.settings = result.data;
          }
          return widget;
        })
      );
    }
    return of(widget);
  }

  getReferralCardWidget(
    id: number,
    name: string,
    title: string,
    secondaryTitle: string,
    subject?: IEntity
  ): Widget {
    return {
      id,
      name,
      metadata: {
        id, // to keep track of widget metadata
        title,
        secondaryTitle,
        subject,
        subjectId: subject ? subject.id : '',
      },
      config: {
        cols: environment.enableGridSizing ? 16 : 8,
        rows: environment.enableGridSizing ? 6 : 3,
      },
      type: 'REFERRALCARD',
    } as Widget;
  }

  getSubjectCardWidget(
    id: number,
    name: string,
    title: string,
    secondaryTitle: string,
    subject?: IEntity
  ): Widget {
    return {
      id,
      name,
      metadata: {
        id, // to keep track of widget metadata
        title,
        secondaryTitle,
        subject,
        subjectId: subject ? subject.id : '',
      },
      config: {
        cols: environment.enableGridSizing ? 16 : 8,
        rows: environment.enableGridSizing ? 6 : 3,
      },
      type: 'SUBJECTCARD',
    } as Widget;
  }

  getLinkedSubjectsWidget(id: number, subject?: IEntity): Widget {
    return {
      id,
      name: 'Linked Subjects',
      metadata: {
        id,
        subject,
        subjectId: subject ? subject.id : '',
      },
      config: {
        cols: environment.enableGridSizing ? 16 : 8,
        rows: environment.enableGridSizing ? 8 : 4,
      },
      type: 'LINKEDSUBJECTS',
    } as Widget;
  }

  getNotesWidget(id: number, subject?: IEntity): Widget {
    return {
      id,
      name: 'Notes',
      metadata: {
        id,
        subject,
        subjectId: subject ? subject.id : '',
      },
      config: {
        cols: environment.enableGridSizing ? 16 : 8,
        rows: environment.enableGridSizing ? 8 : 4,
      },
      type: 'NoteWidgetComponent',
    } as Widget;
  }

  getChecklistWidget(id: number, subject?: IEntity): Widget {
    return {
      id,
      name: 'Checklists',
      metadata: {
        id,
        subject,
        subjectId: subject ? subject.id : '',
      },
      config: {
        cols: environment.enableGridSizing ? 16 : 8,
        rows: environment.enableGridSizing ? 8 : 4,
      },
      type: 'ChecklistWidgetComponent',
    } as Widget;
  }

  getTasksWidget(id: number, subject?: IEntity): Widget {
    return {
      id,
      name: 'Tasks',
      metadata: {
        id,
        subject,
        subjectId: subject ? subject.id : '',
      },
      config: {
        cols: environment.enableGridSizing ? 16 : 8,
        rows: environment.enableGridSizing ? 8 : 4,
      },
      type: 'TaskWidgetComponent',
    } as Widget;
  }

  getReferralCommentsWidget(id: number, subject?: IEntity): Widget {
    return {
      id,
      name: 'Comments',
      metadata: {
        id,
        subject,
        subjectId: subject ? subject.id : '',
      },
      config: {
        cols: environment.enableGridSizing ? 16 : 8,
        rows: environment.enableGridSizing ? 8 : 4,
      },
      type: 'ReferralComments',
    } as Widget;
  }

  getFileListWidget(id: number, subject?: IEntity): Widget {
    return {
      id,
      name: 'Attachments',
      metadata: {
        id,
        subject,
        subjectId: subject ? subject.id : '',
      },
      config: {
        cols: environment.enableGridSizing ? 16 : 8,
        rows: environment.enableGridSizing ? 8 : 4,
      },
      type: 'FileList',
    } as Widget;
  }

  getAssessmentListWidget(id: number, subject?: IEntity) {
    return {
      id,
      name: 'Assessments',
      metadata: {
        id,
        subject,
        subjectId: subject ? subject.id : '',
      },
      config: {
        cols: environment.enableGridSizing ? 16 : 8,
        rows: environment.enableGridSizing ? 8 : 4,
      },
      type: 'AssessmentList',
    } as Widget;
  }

  getAuditListWidget(id: number, subject?: IEntity) {
    return {
      id,
      name: 'Audit Trail',
      metadata: {
        id,
        subject,
        subjectId: subject ? subject.id : '',
      },
      config: {
        cols: environment.enableGridSizing ? 16 : 8,
        rows: environment.enableGridSizing ? 8 : 4,
      },
      type: 'AuditList',
    } as Widget;
  }

  getMarkerListWidget(id: number, subject?: IEntity) {
    return {
      id,
      name: 'Markers',
      metadata: {
        id,
        subject,
        subjectId: subject ? subject.id : '',
      },
      config: {
        cols: environment.enableGridSizing ? 16 : 8,
        rows: environment.enableGridSizing ? 8 : 4,
      },
      type: 'MarkerList',
    } as Widget;
  }

  getProcessListWidget(
    id: number,
    processType: ProcessType,
    subject?: IEntity
  ) {
    return {
      id,
      name: plural(processType.name),
      metadata: {
        id,
        subject,
        subjectId: subject ? subject.id : '',
        processType,
        processTypeId: processType ? processType.id : '',
      },
      config: {
        cols: environment.enableGridSizing ? 16 : 8,
        rows: environment.enableGridSizing ? 8 : 4,
      },
      type: 'ProcessList',
    } as Widget;
  }

  getReviewListWidget(id: number, subject?: IEntity) {
    return {
      id,
      name: 'Reviews',
      metadata: {
        id,
        subject,
        subjectId: subject ? subject.id : '',
      },
      config: {
        cols: environment.enableGridSizing ? 16 : 8,
        rows: environment.enableGridSizing ? 8 : 4,
      },
      type: 'ReviewList',
    } as Widget;
  }

  getLineWidget(id: number) {
    return {
      id,
      name: 'Line',
      config: {
        cols: environment.enableGridSizing ? 8 : 4,
        rows: environment.enableGridSizing ? 2 : 1,
      },
      type: 'LineWidgetComponent',
      metadata: {},
    } as Widget;
  }

  getHtmlWidget(id: number) {
    return {
      id,
      name: 'Text',
      config: {
        cols: environment.enableGridSizing ? 16 : 8,
        rows: environment.enableGridSizing ? 2 : 1,
      },
      type: 'HtmlWidgetComponent',
      metadata: {},
      configurable: true,
    } as Widget;
  }

  // set metadata.subject to correct entity object, if required
  hydrateWidget(widget: Widget, subject?: IEntityWithLinks) {
    if (widget) {
      let subject$;
      if (widget.metadata && !widget.metadata.subject) {
        if (widget.metadata.subjectId) {
          if (
            subject &&
            (subject.id === widget.metadata.subjectId ||
              (subject.isReferral &&
                subject.reference === widget.metadata.subjectId.toString()))
          ) {
            // passed subject matches the entity we want
            subject$ = of(subject);
          } else {
            if (subject && subject.id !== widget.metadata.subjectId) {
              const subjectLink = subject.subjectLinks.find(
                (x) => x.linkedSubjectId === widget.metadata.subjectId
              );
              if (subjectLink) {
                subject$ = of(subjectLink.linkedSubject);
              }
            }
            // if (!subject$){
            //     // MRD - very expensive to call but if all else has failed...
            //     subject$ = this.entityService.getSubjectService(subject).getById(widget.metadata.subjectId);
            // }
          }
        } else {
          subject$ = of(subject);
        }

        if (subject$) {
          subject$.pipe(take(1)).subscribe((entity) => {
            this.subjectService
              .hydrate(entity)
              .pipe(take(1))
              .subscribe((hydratedEntity) => {
                widget.metadata.subject = hydratedEntity;
              });
          });
        }
      }
    }
  }

  // get layout items in correct tab order - ie left to right, top to bottom based on their x and y layout values
  getSortedLayoutItems(array: LayoutItem[]): LayoutItem[] {
    let items = [...array];
    // sort the fields into order by y, then x so that they are in the correct tab order
    items = this.xySort(items, 'y');
    items = this.xySort(items, 'x');
    return items;
  }

  // bubble sort to put layout items into specified order
  // call it twice - once for y, then again for x
  xySort(array: LayoutItem[], sortBy: string): LayoutItem[] {
    let swap = false;
    array = array.slice(); // creates a copy of the array
    // tslint:disable-next-line:prefer-for-of
    for (let i = 0; i < array.length; i++) {
      for (let j = 0; j < array.length - 1; j++) {
        swap = false;
        // sort by Y first ie top to bottom
        const current = array[j].layoutInformation;
        const next = array[j + 1].layoutInformation;

        if (current.y && next.y) {
          if (sortBy === 'y' && current.y > next.y) {
            swap = true;
          }
          // if sorting by x [left to right] then only compare items if they are on the same line ie forename+surname
          else if (
            current.x &&
            next.x &&
            current.y === next.y &&
            current.x > next.x
          ) {
            swap = true;
          }
        }

        if (swap) {
          const s = array[j];
          array[j] = array[j + 1];
          array[j + 1] = s;
        }
      }
    }
    return array;
  }
}
